import React, { useMemo } from 'react';
import { Platform, StyleSheet, Text, View, useWindowDimensions } from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { FontFamily } from '../../../../constants/fonts';
import WesColumnPicker from './components/WesColumnPicker';
import WesLocationMobileTable from './components/WesLocationMobileTable';
import WesLocationTable from './components/WesLocationTable';
import WesPressable from './components/WesPressable';
import WesSectionState, { toSectionStatus } from './components/WesSectionState';
import type { WesLocationTableState } from './useWesLocationTable';
import { useWesLocationDetail } from './useWesLocationDetail';
import { WES_EXPORT_FAILURE, WES_EXPORT_SUCCESS, useWesLocationExport } from './useWesLocationExport';
import type { WesFilterSelection } from './useWesPalletStagingFilters';
import { toDetailView } from './wesPalletStagingMappers';
import type { WesDimKey, WesDisplayMode } from './wesPalletStagingTable';

const MOBILE_DIMENSIONS: WesDimKey[] = ['location'];

const MODES: { key: WesDisplayMode; label: string }[] = [
  { key: 'percent', label: 'Percent' },
  { key: 'pallets', label: 'Pallets' },
];

interface LocationDetailProps {
  applied: WesFilterSelection;
  // Fiscal period-week data, used to translate week tokens to week-in-year.
  periodWeeks: any[];
  table: WesLocationTableState;
}

// Rows, totals, sorting and paging come from the API; the table state here only decides what to ask for. Independent of Staging Scope and chart configuration.
const LocationDetail: React.FC<LocationDetailProps> = ({ applied, periodWeeks, table }) => {
  const { dimensions, mode, sort, page, pageSize, setDimensions, setMode, cycleSort, setPage, setPageSize } = table;
  const { width } = useWindowDimensions();
  // Same mobile convention as the other Navigator reports.
  const isMobileIos = Platform.OS === 'ios' && width < 768;
  const groupDimensions = isMobileIos ? MOBILE_DIMENSIONS : dimensions;

  const detail = useWesLocationDetail({ applied, periodWeeks, dimensions: groupDimensions, sort, mode, page, pageSize });
  const view = useMemo(() => (detail.data ? toDetailView(detail.data, mode) : null), [detail.data, mode]);
  const exporter = useWesLocationExport({ applied, periodWeeks, dimensions: groupDimensions, sort, mode });
  // Export builds an .xlsx in the browser, so it is a desktop web action.
  const canExport = Platform.OS === 'web' && !isMobileIos;
  const exportDisabled = exporter.state === 'working' || !view || view.totalGroups === 0;

  return (
    <View style={styles.card} testID="wes-location-detail">
      <View style={styles.head}>
        <View style={styles.titles}>
          <Text style={styles.title}>Location Detail</Text>
          <Text style={styles.subtitle}>Toggle Percent / Pallets for staged metrics</Text>
        </View>
        <View style={styles.actions}>
          <View style={styles.segmented}>
            {MODES.map((option, index) => {
              const isActive = mode === option.key;
              return (
                <WesPressable
                  key={option.key}
                  style={[styles.segment, index > 0 && styles.segmentDivider, isActive && styles.segmentActive]}
                  onPress={() => setMode(option.key)}
                  accessibilityState={{ selected: isActive }}
                  testID={`wes-mode-${option.key}`}
                >
                  {({ hovered }) => (
                    <Text style={[styles.segmentText, isActive && styles.segmentTextActive, hovered && !isActive && styles.segmentTextHover]}>{option.label}</Text>
                  )}
                </WesPressable>
              );
            })}
          </View>
          {isMobileIos ? null : <WesColumnPicker dimensions={dimensions} onChange={setDimensions} />}
          {canExport ? (
            <WesPressable
              style={[styles.exportBtn, exportDisabled && styles.exportBtnDisabled]}
              hoverStyle={styles.exportBtnHover}
              onPress={exporter.run}
              disabled={exportDisabled}
              accessibilityRole="button"
              accessibilityLabel="Export"
              testID="wes-export-btn"
            >
              {({ hovered }) => (
                <>
                  <MaterialIcons name="file-download" size={18} color={exportDisabled ? '#838383' : hovered ? '#005CBC' : '#616161'} />
                  <Text style={[styles.exportText, exportDisabled && styles.exportTextDisabled, hovered && styles.exportTextHover]}>{exporter.state === 'working' ? 'Exporting…' : 'Export'}</Text>
                </>
              )}
            </WesPressable>
          ) : null}
        </View>
      </View>
      {exporter.state === 'done' || exporter.state === 'error' ? (
        <Text style={[styles.exportStatus, exporter.state === 'error' && styles.exportStatusError]} accessibilityRole="alert" testID="wes-export-status">
          {exporter.state === 'done' ? WES_EXPORT_SUCCESS : WES_EXPORT_FAILURE}
        </Text>
      ) : null}

      <WesSectionState status={toSectionStatus(detail)} skeleton="table" testID="wes-table">
        {view ? (
          isMobileIos ? (
            <WesLocationMobileTable
              rows={view.rows}
              totals={view.totals}
              sort={sort}
              page={page}
              totalGroups={view.totalGroups}
              pageSize={view.pageSize}
              empty={view.totalGroups === 0}
              onSort={cycleSort}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
          ) : (
            <WesLocationTable
              rows={view.rows}
              totals={view.totals}
              dimensions={dimensions}
              sort={sort}
              page={page}
              totalGroups={view.totalGroups}
              pageSize={view.pageSize}
              empty={view.totalGroups === 0}
              onSort={cycleSort}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
          )
        ) : null}
      </WesSectionState>
    </View>
  );
};

const styles = StyleSheet.create({
  card: { backgroundColor: '#FFFFFF', borderRadius: 12, borderWidth: 1, borderColor: '#E5E7EB', padding: 14 },
  head: { flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', rowGap: 12, marginBottom: 10, zIndex: 40 },
  titles: { flexShrink: 1 },
  title: { fontSize: 16, fontFamily: FontFamily.bold, color: '#1A1D21' },
  subtitle: { fontSize: 12, fontFamily: FontFamily.regular, color: '#838383' },
  actions: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'flex-start', columnGap: 10, rowGap: 8 },
  segmented: { flexDirection: 'row', borderWidth: 1, borderColor: '#D0D9E8', borderRadius: 8, overflow: 'hidden' },
  segment: { paddingHorizontal: 12, paddingVertical: 8, backgroundColor: '#FFFFFF' },
  segmentDivider: { borderLeftWidth: 1, borderLeftColor: '#D0D9E8' },
  segmentActive: { backgroundColor: '#005CBC' },
  segmentText: { fontSize: 12, fontFamily: FontFamily.medium, color: '#2D2D2D' },
  segmentTextActive: { color: '#FFFFFF' },
  segmentTextHover: { color: '#005CBC' },
  exportBtn: { flexDirection: 'row', alignItems: 'center', columnGap: 6, height: 30, paddingHorizontal: 14, borderRadius: 999, borderWidth: 1, borderColor: '#D0D0D0', backgroundColor: '#FFFFFF' },
  exportBtnHover: { borderColor: '#005CBC' },
  exportBtnDisabled: { opacity: 0.5 },
  exportText: { fontSize: 12.5, fontWeight: '600', fontFamily: FontFamily.medium, color: '#616161' },
  exportTextHover: { color: '#005CBC' },
  exportTextDisabled: { color: '#838383' },
  exportStatus: { alignSelf: 'flex-end', marginBottom: 8, fontSize: 12, fontFamily: FontFamily.medium, color: '#0C420B', backgroundColor: '#CAF3C6', borderRadius: 4, paddingHorizontal: 8, paddingVertical: 3 },
  exportStatusError: { color: '#AB2B09', backgroundColor: '#FFE5DE' },
});

export default LocationDetail;
